import { apiSlice } from "../store/api/adminApi"

export const organizerApi = apiSlice.injectEndpoints({
    endpoints: (build)=>({
        fetchOrganizers: build.query<any, any>({
            query: (body: {
                type: string;
                skip: number;
                take: number;
                filter: string;
                organizerId?: number;
            }) => ({
                url: `/admin/organizers/${body.type}?${body?.organizerId ? `organizerId=${body.organizerId}&` : ''}skip=${body.skip || 0}&take=${body.take || 10}${body.filter || ""}`
            }),
        }),
        autoConfirmation: build.mutation({
            query: (id: string | number) => ({
                url: `/admin/users/auto-confirmation/${id}`,
                method: 'PUT',
            }),
        }),
        getRobotSmsTotalCredits: build.query({
            query: () => ({
                url: `/admin/users/robot-sms/credits/total`,
                method: 'GET',
            }),
        }),
        updateRobotSmsAccess: build.mutation({
            query: ({ id, isCanUseRobotSms }: { id: string | number, isCanUseRobotSms: boolean }) => ({
                url: `/admin/users/${id}/robot-sms/access`,
                method: 'PUT',
                body: {
                    isCanUseRobotSms,
                }
            }),
        }),
        updateRobotSmsCredits: build.mutation({
            query: ({ id, robotSmsCredits }: { id: string | number, robotSmsCredits: number }) => ({
                url: `/admin/users/${id}/robot-sms/credits`,
                method: 'PUT',
                body: {
                    robotSmsCredits,
                }
            }),
        }),
        updateOrganizerSettingsEventFee: build.mutation({
            query: ({ userId, feePercent }: { userId: string | number, feePercent: number }) => ({
                url: `/admin/organizers/${userId}/settings/fee-percent`,
                method: 'PUT',
                body: {
                    feePercent,
                }
            }),
        }),
        openOrganizerAsAdmin: build.query<{}, {id: number}>({
            query: ({id}) => ({
                  url: `/admin/open/user/${id}`
            }),
        }),
        getGuestListSearch: build.mutation({
            query: ({ phone, eventLink }: { phone: string, eventLink: string }) => ({
                url: `/admin/guest/list`,
                method: 'POST',
                body: {
                    phone,
                    eventLink,
                }
            }),
        }),
        cancelTicket: build.mutation({
            query: ({ sessionId, ids, sendSms, yeshInvoiceDocs, returnMoney, fullReturn }: { sessionId: string, ids: number[]; sendSms: boolean; yeshInvoiceDocs: boolean; returnMoney: boolean; fullReturn: boolean; }) => ({
                url: `/admin/guest/${sessionId}/cancel`,
                method: 'PUT',
                body: {
                    ids,
                    sendSms,
                    yeshInvoiceDocs,
                    returnMoney,
                    fullReturn,
                }
            }),
        }),
        getGuestDetails: build.mutation({
            query: ({ sessionId }: { sessionId: string }) => ({
                url: `/admin/guest/${sessionId}`,
            }),
        }),

        updateOrganizerSettings: build.mutation({
            query: ({ 
                organizerId,
                userId,
                isEarlyWithdrawalEnabled,
                earlyWithdrawalFee,
                earlyWithdrawalDays,
                earlyWithdrawalPercent,
                freeTransactionCount,
                transactionMinSum,
                transactionFee,
                fastWithdrawalFee,
                withdrawalType,
                withdrawalDays,
                adminNote,
            }: { 
                organizerId?: number; 
                userId: number; 
                isEarlyWithdrawalEnabled: boolean;
                earlyWithdrawalFee?: number | null;
                earlyWithdrawalDays?: number | null;
                earlyWithdrawalPercent?: number | null;
                freeTransactionCount: number;
                transactionMinSum: number;
                transactionFee: number;
                fastWithdrawalFee: number;
                withdrawalType: string;
                withdrawalDays?: number | null;
                adminNote?: string | null;
            }) => ({
                url: `/admin/organizers/${userId}/settings/update`,
                method: 'PUT',
                body: {
                    organizerId,
                    isEarlyWithdrawalEnabled,
                    earlyWithdrawalFee,
                    earlyWithdrawalDays,
                    earlyWithdrawalPercent,
                    freeTransactionCount,
                    transactionMinSum,
                    transactionFee,
                    fastWithdrawalFee,
                    withdrawalType,
                    withdrawalDays,
                    adminNote,
                }
            }),
        }),
        getOrganizerTransactionList: build.query({
            query: ({ organizerId, type, transactionId, skip, take }: { organizerId: number; type: string; transactionId?: number; skip?: number; take?: number; }) => ({
                url: `/admin/organizers/${organizerId}/transaction/list/${type}?skip=${skip || 0}&take=${take || 10}${transactionId ? `&transactionId=${transactionId}` : ''}`,
            }),
        }),

        getOrganizerRequestList: build.query({
            query: ({ organizerId, skip, take }: { organizerId?: number; skip?: number; take?: number; }) => ({
                url: `/admin/organizers/early-withdrawal/request/list?skip=${skip || 0}&take=${take || 10}${organizerId ? `&organizerId=${organizerId}` : ''}`,
            }),
        }),

        approveOrganizerRequest: build.mutation({
            query: ({ organizerId, data }) => ({
                url: `/admin/organizers/${organizerId}/early-withdrawal/approve`,
                method: 'PUT',
                body: data,
            }),
        }),
        
        declineOrganizerRequest: build.mutation({
            query: ({ organizerId, text = null }) => ({
                url: `/admin/organizers/${organizerId}/early-withdrawal/decline`,
                method: 'PUT',
                body: { text },
            }),
        }),
        
        uploadOrganizerFile: build.mutation({
            query: ({ userId, data }) => ({
                url: `/admin/organizers/${userId}/file`,
                method: 'POST',
                body: data,
            }),
        }),
        
        deleteOrganizerFile: build.mutation({
            query: ({ userId, fileId }) => ({
                url: `/admin/organizers/${userId}/file/${fileId}`,
                method: 'DELETE',
            }),
        }),

        getOrganizerEventList: build.query({
            query: ({ userId, search, skip, take }: { userId: number; search: string; skip?: number; take?: number; }) => ({
                url: `/admin/organizers/${userId}/event/list/?skip=${skip || 0}&take=${take || 10}${search ? `&search=${search}` : ''}`,
            }),
        }),

        getOrganizerBillAccountList: build.query({
            query: ({ userId, search, skip, take }: { userId: number; search?: string; skip?: number; take?: number; }) => ({
                url: `/admin/organizers/${userId}/bill-account/list/?skip=${skip || 0}&take=${take || 10}${search ? `&search=${search}` : ''}`,
            }),
        }),

        getOrganizerEventInfo: build.query({
            query: ({ userId, eventLinkId }: { userId: number; eventLinkId: string; }) => ({
                url: `/admin/organizers/${userId}/event/${eventLinkId}/info`,
            }),
        }),

        calculateOrganizerEventTransaction: build.mutation({
            query: ({ userId, eventLinkId, data }: { userId: number; eventLinkId: string; data: any }) => ({
                url: `/admin/organizers/${userId}/event/${eventLinkId}/transaction/calculate`,
                method: 'POST',
                body: data,
            }),
        }),

        createOrganizerEventTransaction: build.mutation({
            query: ({ userId, eventLinkId, data }: { userId: number; eventLinkId: string; data: any }) => ({
                url: `/admin/organizers/${userId}/event/${eventLinkId}/transaction/create`,
                method: 'POST',
                body: data,
            }),
        }),

    })
})